import localforage from 'localforage'
import type { QueuedAction } from '@/data/schema/stocktake'

export class OfflineService {
  private static instance: OfflineService
  private isOnline: boolean = navigator.onLine
  // eslint-disable-next-line no-unused-vars
  private listeners: Set<(isOnline: boolean) => void> = new Set()
  private queueNamespace = 'stocktake_offline_queue'
  private statusNamespace = 'stocktake_offline_status'

  private constructor() {
    localforage.config({ name: 'stocktakeApp', storeName: this.queueNamespace })
    this.setupListeners()
  }

  static getInstance(): OfflineService {
    if (!OfflineService.instance) {
      OfflineService.instance = new OfflineService()
    }
    return OfflineService.instance
  }

  private setupListeners() {
    window.addEventListener('online', () => this.setOnline(true))
    window.addEventListener('offline', () => this.setOnline(false))
  }

  private setOnline(online: boolean) {
    if (this.isOnline !== online) {
      this.isOnline = online
      this.listeners.forEach((listener) => listener(online))
    }
  }

  getIsOnline(): boolean {
    return this.isOnline
  }

  // eslint-disable-next-line no-unused-vars
  onStatusChange(callback: (isOnline: boolean) => void): () => void {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  async addToQueue(action: QueuedAction): Promise<void> {
    const queue = (await this.getQueue()) || []
    queue.push(action)
    await localforage.setItem(this.queueNamespace, queue)
  }

  async getQueue(): Promise<QueuedAction[]> {
    return (await localforage.getItem<QueuedAction[]>(this.queueNamespace)) || []
  }

  async removeFromQueue(queueId: string): Promise<void> {
    const queue = (await this.getQueue()) || []
    const filtered = queue.filter((item) => item.queueId !== queueId)
    await localforage.setItem(this.queueNamespace, filtered)
  }

  async clearQueue(): Promise<void> {
    await localforage.setItem(this.queueNamespace, [])
  }

  async getQueueLength(): Promise<number> {
    const queue = await this.getQueue()
    return queue.length
  }

  async setItemSyncStatus(itemId: string | number, syncing: boolean): Promise<void> {
    const status = (await localforage.getItem<Record<string, boolean>>(this.statusNamespace)) || {}
    if (syncing) {
      status[itemId] = true
    } else {
      delete status[itemId]
    }
    await localforage.setItem(this.statusNamespace, status)
  }

  async getItemSyncStatus(itemId: string | number): Promise<boolean> {
    const status = (await localforage.getItem<Record<string, boolean>>(this.statusNamespace)) || {}
    return status[itemId] || false
  }

  async clearItemSyncStatus(): Promise<void> {
    await localforage.setItem(this.statusNamespace, {})
  }
}

export default OfflineService.getInstance()
